import * as React from 'react';
import { HAFTA_GUNLERI, HAFTA_SONU, gunAralik } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde } from '@/components/ui/card';
import { ApiHatasi, api } from '@/lib/api';
import { paraKisa } from '@/lib/utils';

/**
 * Fiyat takvimi + toplu degisiklik.
 *
 * Tasarim kaynagi: Claude Design - "otelcms Fiyat Takvimi.dc.html".
 * Tasarimdaki fiyatlar ve doluluklar ureticiydi; buradaki her sey gercek:
 *   GET  /api/fiyatlar/planlar  -> oda tipi + fiyat plani sekmeleri
 *   GET  /api/fiyatlar          -> gunluk fiyat, min. kalis, doluluk
 *   POST /api/fiyatlar/toplu    -> secili gunlere toplu uygula
 *
 * Toplu degisiklik Airbnb'deki gibi: bir aralik secilir, istege bagli olarak
 * yalnizca belirli haftagunlerine uygulanir ve fiyat sabit ya da yuzde
 * olarak verilir. Tasarimdaki "Bu haftaya uygula" dugmesi bunun dar bir
 * ozel haliydi; burasi genel hali.
 */

interface Plan {
  id: string;
  ad: string;
  roomTypeId: string;
  odaTipiAd: string;
  odaTipiKod: string;
}

interface Gun {
  tarih: string;
  fiyatKurus: number | null;
  minKalis: number;
  satisaKapali: boolean;
  dolu: number;
  dolulukYuzde: number;
  gecmis: boolean;
}

const AY_ADI = (d: Date) => d.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
const ayBasi = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const iso = (d: Date) => d.toISOString().slice(0, 10);

function ayAralik(ay: Date): { baslangic: string; bitis: string } {
  const bas = ayBasi(ay);
  const son = new Date(Date.UTC(bas.getUTCFullYear(), bas.getUTCMonth() + 1, 0));
  return { baslangic: iso(bas), bitis: iso(son) };
}

/** Doluluk cubugunun rengi: yuksek doluluk fiyat artirma sinyalidir. */
function dolulukRengi(yuzde: number): string {
  if (yuzde >= 80) return '#e5484d';
  if (yuzde >= 60) return '#f2b632';
  return '#1f4fd8';
}

export function FiyatTakvimi({ yetkiler }: { yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('rate:write');
  const [planlar, setPlanlar] = React.useState<Plan[]>([]);
  const [planId, setPlanId] = React.useState<string | null>(null);
  const [ay, setAy] = React.useState(() => ayBasi(new Date()));
  const [gunler, setGunler] = React.useState<Gun[]>([]);
  const [toplamOda, setToplamOda] = React.useState(0);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  /** Takvimden secilen gunler; toplu islem bunlara uygulanabilir. */
  const [secili, setSecili] = React.useState<Set<string>>(new Set());
  const [sonuc, setSonuc] = React.useState<string | null>(null);

  React.useEffect(() => {
    void api<{ planlar: Plan[] }>('/api/fiyatlar/planlar')
      .then((v) => {
        setPlanlar(v.planlar);
        setPlanId((o) => o ?? v.planlar[0]?.id ?? null);
      })
      .catch((e) => setHata(e instanceof ApiHatasi ? e.message : 'Fiyat planları alınamadı.'));
  }, []);

  const aralik = React.useMemo(() => ayAralik(ay), [ay]);

  const yukle = React.useCallback(async () => {
    if (!planId) return;
    setYukleniyor(true);
    try {
      const v = await api<{ gunler: Gun[]; toplamOda: number }>(
        `/api/fiyatlar?ratePlanId=${planId}&baslangic=${aralik.baslangic}&bitis=${aralik.bitis}`,
      );
      setGunler(v.gunler);
      setToplamOda(v.toplamOda);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Takvim alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, [planId, aralik]);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  // Ay ya da plan degisince secim anlamini yitirir.
  React.useEffect(() => {
    setSecili(new Set());
  }, [planId, aralik.baslangic]);

  function gunSec(tarih: string) {
    setSecili((o) => {
      const y = new Set(o);
      if (y.has(tarih)) y.delete(tarih);
      else y.add(tarih);
      return y;
    });
  }

  const dolular = gunler.filter((g) => g.fiyatKurus !== null);
  const ortalama = dolular.length
    ? Math.round(dolular.reduce((t, g) => t + (g.fiyatKurus ?? 0), 0) / dolular.length)
    : 0;
  const ortDoluluk = gunler.length
    ? Math.round(gunler.reduce((t, g) => t + g.dolulukYuzde, 0) / gunler.length)
    : 0;

  // Takvim izgarasi Pazartesi baslar; ayin ilk gunu icin bosluk birakilir.
  const ilkGun = gunler[0] ? new Date(`${gunler[0].tarih}T00:00:00Z`).getUTCDay() : 1;
  const bosluk = (ilkGun + 6) % 7;

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Fiyat takvimi</h1>

        {planlar.length > 1 && (
          <div className="flex w-full gap-1 rounded-full border border-cizgi bg-white p-1 sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {planlar.map((p) => (
              <button
                key={p.id}
                onClick={() => setPlanId(p.id)}
                className={
                  'whitespace-nowrap rounded-full px-4 py-[7px] text-[13px] font-semibold transition-colors ' +
                  (planId === p.id ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
                }
              >
                {p.odaTipiAd}
              </button>
            ))}
          </div>
        )}

        <div className="inline-flex items-center overflow-hidden rounded-xl border border-cizgi bg-white">
          <button
            onClick={() => setAy((a) => new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth() - 1, 1)))}
            aria-label="Önceki ay"
            className="px-3 py-2.5 text-gri transition-colors hover:text-lacivert"
          >
            ‹
          </button>
          <span className="min-w-[130px] px-2 text-center text-sm font-semibold">{AY_ADI(ay)}</span>
          <button
            onClick={() => setAy((a) => new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth() + 1, 1)))}
            aria-label="Sonraki ay"
            className="px-3 py-2.5 text-gri transition-colors hover:text-lacivert"
          >
            ›
          </button>
        </div>

        <div className="flex w-full flex-wrap gap-x-4 gap-y-1 text-[13px] font-medium text-gri lg:ml-auto lg:w-auto">
          <span>
            Ort. fiyat <strong className="text-lacivert">{ortalama ? paraKisa(ortalama) : '—'}</strong>
          </span>
          <span>
            Ort. doluluk <strong className="text-lacivert">%{ortDoluluk}</strong>
          </span>
          <span>
            {toplamOda} oda · {AY_ADI(ay)}
          </span>
        </div>
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}
      {sonuc && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-emerald-900">{sonuc}</div>
      )}

      <div className="grid items-start gap-[18px] xl:grid-cols-[minmax(0,1fr)_340px]">
        <Kart className="[animation:rise_.6s_.1s_cubic-bezier(.2,.7,.2,1)_both]">
          {/*
            Yedi sutunlu takvim 390px'de hucre basina ~48px'e dusuyor ve
            fiyat ("₺4.200") hucreden tasiyordu. Sutuna en az genislik
            verip izgarayi yatay kaydiriyoruz; ay gorunumu bozulmadan
            okunur kaliyor.
          */}
          <KartGovde className="overflow-x-auto p-3 sm:p-5 [-webkit-overflow-scrolling:touch]">
            <div className="min-w-[520px]">
            <div className="mb-2 grid grid-cols-7 gap-2">
              {HAFTA_GUNLERI.map((g) => (
                <span
                  key={g.gun}
                  className={
                    'p-1 text-center text-[11px] font-bold uppercase tracking-[.08em] ' +
                    (g.gun === 0 || g.gun === 6 ? 'text-marka-600' : 'text-gri')
                  }
                >
                  {g.kisa}
                </span>
              ))}
            </div>

            {yukleniyor ? (
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 35 }, (_, i) => (
                  <div key={i} className="h-24 animate-pulse rounded-[14px] bg-satir" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: bosluk }, (_, i) => (
                  <span key={`bos-${i}`} />
                ))}
                {gunler.map((g) => {
                  const isaretli = secili.has(g.tarih);
                  return (
                    <button
                      key={g.tarih}
                      onClick={() => gunSec(g.tarih)}
                      className={
                        'flex min-h-24 flex-col gap-1.5 rounded-[14px] border-[1.5px] p-2.5 text-left transition ' +
                        (isaretli
                          ? 'border-marka-600 bg-mavi-zemin'
                          : g.satisaKapali
                            ? 'border-dashed border-[#8a93ad] bg-zemin'
                            : g.minKalis > 1
                              ? 'border-[#f2d99a] bg-white'
                              : 'border-cizgi bg-white') +
                        (g.gecmis ? ' opacity-55' : '')
                      }
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[13px] font-bold">
                          {Number(g.tarih.slice(8, 10))}
                        </span>
                        {g.minKalis > 1 && (
                          <span className="shrink-0 rounded-full bg-[#fff5dc] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#b9840f]">
                            {g.minKalis}
                          </span>
                        )}
                      </div>

                      {g.satisaKapali ? (
                        <span className="mt-auto text-xs font-bold text-[#8a93ad]">Kapalı</span>
                      ) : (
                        <>
                          <span className="truncate text-[13.5px] font-bold tabular-nums tracking-[-.02em] sm:text-[15px]">
                            {g.fiyatKurus !== null ? paraKisa(g.fiyatKurus) : '—'}
                          </span>
                          <div className="mt-auto flex items-end gap-1.5">
                            <div className="h-1 flex-1 overflow-hidden rounded-sm bg-satir">
                              <div
                                className="h-full transition-[width] duration-500"
                                style={{
                                  width: `${g.dolulukYuzde}%`,
                                  background: dolulukRengi(g.dolulukYuzde),
                                }}
                              />
                            </div>
                            <span className="text-[10.5px] font-semibold text-gri">%{g.dolulukYuzde}</span>
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            </div>
          </KartGovde>
        </Kart>

        <TopluPanel
          planId={planId}
          aralik={aralik}
          secili={secili}
          yazabilir={yazabilir}
          onTemizle={() => setSecili(new Set())}
          onUygulandi={async (mesaj) => {
            setSonuc(mesaj);
            setTimeout(() => setSonuc(null), 4000);
            setSecili(new Set());
            await yukle();
          }}
        />
      </div>
    </main>
  );
}

/**
 * Toplu degisiklik paneli.
 *
 * Iki calisma bicimi vardir ve ikisi de tek istekte biter:
 *   - takvimden gun secilmisse YALNIZCA o gunlere,
 *   - secim yoksa gorunen ayin tamamina (istege bagli gun suzgeciyle).
 */
function TopluPanel({
  planId,
  aralik,
  secili,
  yazabilir,
  onTemizle,
  onUygulandi,
}: {
  planId: string | null;
  aralik: { baslangic: string; bitis: string };
  secili: Set<string>;
  yazabilir: boolean;
  onTemizle: () => void;
  onUygulandi: (mesaj: string) => void | Promise<void>;
}) {
  const [mod, setMod] = React.useState<'sabit' | 'yuzde'>('sabit');
  const [fiyat, setFiyat] = React.useState('');
  const [yuzde, setYuzde] = React.useState('');
  const [minKalis, setMinKalis] = React.useState<number | null>(null);
  const [satis, setSatis] = React.useState<'acik' | 'kapali' | null>(null);
  const [gunSuzgeci, setGunSuzgeci] = React.useState<number[]>([]);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  // Takvimden secim yapildiysa gun suzgeci anlamsizlasir: secim zaten kesin.
  const secimVar = secili.size > 0;
  const hedefAdet = secimVar
    ? secili.size
    : gunSuzgeci.length
      ? gunAralik(aralik.baslangic, aralik.bitis).filter((g) =>
          gunSuzgeci.includes(new Date(`${g}T00:00:00Z`).getUTCDay()),
        ).length
      : gunAralik(aralik.baslangic, aralik.bitis).length;

  const birSeyVar =
    (mod === 'sabit' && fiyat.trim() !== '') ||
    (mod === 'yuzde' && yuzde.trim() !== '') ||
    minKalis !== null ||
    satis !== null;

  async function uygula() {
    if (!planId || !birSeyVar || gonderiliyor) return;
    setGonderiliyor(true);
    setHata(null);

    /*
     * Secili gunler bitisik olmayabilir (ornegin ayin 3'u, 9'u, 21'i).
     * Sunucu aralik + gun suzgeci aliyor; bu yuzden secim varken her gun
     * icin ayri, tek gunluk bir aralik gonderilir. Gun sayisi bir ayla
     * sinirli oldugu icin bu, karmasik bir "tarih listesi" sozlesmesi
     * eklemekten daha basit ve okunakli.
     */
    const govdeler = secimVar
      ? [...secili].sort().map((t) => ({ baslangic: t, bitis: t, gunler: undefined }))
      : [{ baslangic: aralik.baslangic, bitis: aralik.bitis, gunler: gunSuzgeci.length ? gunSuzgeci : undefined }];

    const ortak = {
      ratePlanId: planId,
      ...(mod === 'sabit' && fiyat.trim() !== ''
        ? { fiyatKurus: Math.round(Number(fiyat) * 100) }
        : {}),
      ...(mod === 'yuzde' && yuzde.trim() !== '' ? { yuzde: Number(yuzde) } : {}),
      ...(minKalis !== null ? { minKalis } : {}),
      ...(satis !== null ? { satisaKapali: satis === 'kapali' } : {}),
    };

    try {
      let toplam = 0;
      for (const g of govdeler) {
        const cevap = await api<{ etkilenen: number }>('/api/fiyatlar/toplu', {
          method: 'POST',
          body: JSON.stringify({ ...ortak, ...g }),
        });
        toplam += cevap.etkilenen;
      }
      await onUygulandi(`${toplam} gün güncellendi.`);
      setFiyat('');
      setYuzde('');
      setMinKalis(null);
      setSatis(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Uygulanamadı.');
    } finally {
      setGonderiliyor(false);
    }
  }

  if (!yazabilir) {
    return (
      <Kart>
        <KartGovde>
          <p className="text-[13px] leading-relaxed text-gri">
            Fiyat değiştirme yetkiniz yok. Takvimi görüntüleyebilirsiniz.
          </p>
        </KartGovde>
      </Kart>
    );
  }

  return (
    <Kart className="[animation:rise_.6s_.15s_cubic-bezier(.2,.7,.2,1)_both] xl:sticky xl:top-[92px]">
      <KartBaslik>
        <h2 className="text-[17px] font-bold tracking-[-.01em]">Toplu değiştir</h2>
        {secimVar && (
          <button onClick={onTemizle} className="ml-auto text-[13px] font-semibold text-marka-600">
            Seçimi temizle
          </button>
        )}
      </KartBaslik>
      <KartGovde className="flex flex-col gap-4">
        <div className="rounded-xl bg-zemin px-3.5 py-2.5 text-[13px] leading-relaxed text-gri">
          {secimVar ? (
            <>
              Takvimden <strong className="text-lacivert">{secili.size} gün</strong> seçtiniz.
            </>
          ) : (
            <>
              Görünen ayın <strong className="text-lacivert">{hedefAdet} günü</strong> etkilenecek. Belirli
              günleri seçmek için takvime dokunun.
            </>
          )}
        </div>

        {!secimVar && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">Sadece bu günler</span>
            <div className="flex flex-wrap gap-1.5">
              {HAFTA_GUNLERI.map((g) => {
                const secik = gunSuzgeci.includes(g.gun);
                return (
                  <button
                    key={g.gun}
                    onClick={() =>
                      setGunSuzgeci((o) => (secik ? o.filter((x) => x !== g.gun) : [...o, g.gun]))
                    }
                    aria-pressed={secik}
                    className={
                      'rounded-lg px-2.5 py-1.5 text-xs font-bold transition-colors ' +
                      (secik ? 'bg-lacivert text-white' : 'bg-zemin text-gri')
                    }
                  >
                    {g.kisa}
                  </button>
                );
              })}
              <button
                onClick={() => setGunSuzgeci((o) => (o.length === 2 && o.includes(5) ? [] : [...HAFTA_SONU]))}
                className="rounded-lg border border-cizgi px-2.5 py-1.5 text-xs font-semibold text-marka-600"
              >
                Hafta sonu
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex gap-1 rounded-full bg-zemin p-1">
            {(
              [
                ['sabit', 'Sabit fiyat'],
                ['yuzde', 'Yüzde değiştir'],
              ] as const
            ).map(([k, etiket]) => (
              <button
                key={k}
                onClick={() => setMod(k)}
                className={
                  'flex-1 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors ' +
                  (mod === k ? 'bg-white text-lacivert shadow-sm' : 'text-gri')
                }
              >
                {etiket}
              </button>
            ))}
          </div>

          {mod === 'sabit' ? (
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-slate-700">Gecelik fiyat (₺)</span>
              <input
                value={fiyat}
                onChange={(e) => setFiyat(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                placeholder="2400"
                className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-base tabular-nums focus:border-marka-500 focus:outline-none focus:ring-2 focus:ring-marka-100"
              />
            </label>
          ) : (
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-slate-700">Değişim (%)</span>
              <input
                value={yuzde}
                onChange={(e) => setYuzde(e.target.value.replace(/[^0-9-]/g, ''))}
                inputMode="numeric"
                placeholder="15"
                className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-base tabular-nums focus:border-marka-500 focus:outline-none focus:ring-2 focus:ring-marka-100"
              />
              <span className="text-sm text-slate-500">
                Eksi değer indirim yapar (−10). Fiyatı olmayan güne uygulanmaz.
              </span>
            </label>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-slate-700">Min. konaklama</span>
          <div className="flex gap-1.5">
            {[1, 2, 3, 5, 7].map((v) => (
              <button
                key={v}
                onClick={() => setMinKalis((o) => (o === v ? null : v))}
                aria-pressed={minKalis === v}
                className={
                  'size-9 rounded-lg text-sm font-bold transition-colors ' +
                  (minKalis === v ? 'bg-sari text-lacivert' : 'bg-zemin text-gri')
                }
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-slate-700">Satış</span>
          <div className="flex gap-1.5">
            {(
              [
                ['acik', 'Satışa aç'],
                ['kapali', 'Satışa kapat'],
              ] as const
            ).map(([k, etiket]) => (
              <button
                key={k}
                onClick={() => setSatis((o) => (o === k ? null : k))}
                aria-pressed={satis === k}
                className={
                  'flex-1 rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors ' +
                  (satis === k
                    ? k === 'kapali'
                      ? 'bg-kirmizi text-white'
                      : 'bg-yesil text-white'
                    : 'bg-zemin text-gri')
                }
              >
                {etiket}
              </button>
            ))}
          </div>
        </div>

        {hata && <p className="text-sm text-red-600">{hata}</p>}

        <Button onClick={uygula} disabled={!birSeyVar || !planId} yukleniyor={gonderiliyor} boyut="lg">
          {hedefAdet} güne uygula
        </Button>
      </KartGovde>
    </Kart>
  );
}
